import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import PendingApproval from "@/pages/PendingApproval";
import Dashboard from "@/pages/Dashboard";
import PBOList from "@/pages/PBOList";
import PBOAdd from "@/pages/PBOAdd";
import PBODetail from "@/pages/PBODetail";
import Admin from "@/pages/Admin";
import LaunchReset from "@/pages/LaunchReset";
import Settings from "@/pages/Settings";
import Activity from "@/pages/Activity";
import Conferences from "@/pages/Conferences";
import Home from "@/pages/Home";
import ATLHub from "@/pages/ATLHub";
import Bootcamp from "@/pages/Bootcamp";
import Vetting from "@/pages/Vetting";
import Appointments from "@/pages/Appointments";
import PartnerProfile from "@/pages/PartnerProfile";
import MilestoneReport from "@/pages/MilestoneReport";
import PrivacyNotice from "@/pages/PrivacyNotice";
import Network from "@/pages/Network";
import Leadership from "@/pages/Leadership";
import NewPartnerGuide from "@/pages/NewPartnerGuide";
import TalkLibrary from "@/pages/TalkLibrary";
import OrgAdmin from "@/pages/OrgAdmin";
import PrivacyAcknowledge from "@/pages/PrivacyAcknowledge";
import "@/App.css";

// Reads hash reactively via react-router's useLocation, catching session_id fragment before any protected route.
function OAuthGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

function Protected({ children, adminOnly, orgAdminOnly, requireOnboarded = true, requireApproved = true }) {
  const { user, loading, privacy } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
  // Belt-and-braces: a user is only truly onboarded if they also have both
  // required hierarchy fields on file. This blocks any stale account that
  // was created under an earlier onboarding build and is missing team/upline.
  const hierarchyIncomplete =
    user.role !== "admin" && user.role !== "oversight" &&
    (!user.upline_team || !user.nearest_upline_leader_id);
  if (requireOnboarded && (!user.onboarded || hierarchyIncomplete) && user.role !== "admin")
    return <Navigate to="/onboarding" replace />;
  if (requireApproved && !user.approved && user.role !== "admin") return <Navigate to="/pending-approval" replace />;
  if (requireApproved && privacy?.required) return <PrivacyAcknowledge />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  if (orgAdminOnly && !(user.role === "platinum" && user.platinum_admin)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <OAuthGate>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/privacy" element={<PrivacyNotice />} />
            <Route path="/onboarding" element={<Protected requireOnboarded={false} requireApproved={false}><Onboarding /></Protected>} />
            <Route path="/pending-approval" element={<Protected requireOnboarded={false} requireApproved={false}><PendingApproval /></Protected>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route index element={<Home />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="pbos" element={<PBOList />} />
              <Route path="pbos/new" element={<PBOAdd />} />
              <Route path="pbos/:id" element={<PBODetail />} />
              <Route path="activity" element={<Activity />} />
              <Route path="conferences" element={<Conferences />} />
              <Route path="hub" element={<ATLHub />} />
              <Route path="bootcamp" element={<Bootcamp />} />
              <Route path="vetting" element={<Vetting />} />
              <Route path="vetting/:stageId" element={<Vetting />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="appointments/:stageId" element={<Appointments />} />
              <Route path="network" element={<Network />} />
              <Route path="network/:stageId" element={<Network />} />
              <Route path="leadership" element={<Leadership />} />
              <Route path="leadership/:stageId" element={<Leadership />} />
              <Route path="new-partner-guide" element={<NewPartnerGuide />} />
              <Route path="new-partner-guide/:stageId" element={<NewPartnerGuide />} />
              <Route path="talk-library" element={<TalkLibrary />} />
              <Route path="talk-library/:listId" element={<TalkLibrary />} />
              <Route path="people/:id" element={<PartnerProfile />} />
              <Route path="milestones" element={<MilestoneReport />} />
              <Route path="settings" element={<Settings />} />
              <Route path="admin" element={<Protected adminOnly><Admin /></Protected>} />
              <Route path="org-admin" element={<Protected orgAdminOnly><OrgAdmin /></Protected>} />
              <Route path="admin/launch-reset" element={<Protected adminOnly><LaunchReset /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </OAuthGate>
      </BrowserRouter>
      <Toaster position="top-center" richColors closeButton offset={72} />
    </AuthProvider>
  );
}
